/**
 * Synexa web client logger.
 *
 * The one logging API components and services call. It always writes to the
 * console; once `main.ts` has installed a {@link TelemetrySink} (Sentry) it also
 * forwards to that. Same API as the browser extension's logger, so events from
 * both clients read the same in Sentry.
 *
 * Rules for callers:
 *  - Messages are generic ('Send link failed'). Never put a patient name, MRN
 *    or DOB in a message — titles surface in alert emails and issue lists.
 *    Patient details travel in {@link logger.setPatient} context instead.
 *  - Never log note text, ICD/CPT lists, test answers, payloads or tokens.
 */

export type LogPrimitive = string | number | boolean | null | undefined;
export type LogContext = Record<string, LogPrimitive>;
export type LogLevel = 'info' | 'warning' | 'error';

export interface TelemetryUser {
  id: string;
  email?: string | null;
  username?: string | null;
  userType?: string | null;
  roles?: string[];
  orgId?: string | null;
  orgName?: string | null;
}

export interface PatientContext {
  patientId?: string | null;
  name?: string | null;
  mrn?: string | null;
  dob?: string | null;
}

export interface EpisodeContext {
  episodeId?: string | null;
  serviceName?: string | null;
  status?: string | null;
  stage?: string | null;
  testCode?: string | null;
}

export interface FacilityContext {
  facilityId?: string | null;
  facilityName?: string | null;
  orgId?: string | null;
  orgName?: string | null;
}

export interface FlowSnapshot {
  name: string;
  flowId: string;
  startedAt: number;
}

/** Everything a sink needs to record one event, snapshotted at call time. */
export interface TelemetryRecord {
  level: LogLevel;
  message: string;
  error?: unknown;
  context: LogContext;
  patient: PatientContext | null;
  episode: EpisodeContext | null;
  facility: FacilityContext | null;
  flow: FlowSnapshot | null;
  /** Groups repeated events (e.g. every successful run of a flow) into one issue. */
  fingerprint?: string[];
}

export interface TelemetryBreadcrumb {
  category: string;
  message: string;
  level?: LogLevel;
  data?: LogContext;
}

/**
 * What the user is doing right now, e.g. 'Push to EMR' for 'Jane Doe'. While a
 * flow runs, sinks label everything with it — including the replay, which
 * Sentry lists by user display name, so the replay list reads as
 * 'Sara Khan · Push to EMR · Pt: Jane Doe (MRN 123)' instead of 'Anonymous'.
 */
export interface TelemetryActivity {
  flow: FlowSnapshot;
  /** Human label for the flow, e.g. 'Push to EMR'. */
  label: string;
  patient: PatientContext | null;
  episode: EpisodeContext | null;
  facility: FacilityContext | null;
}

/** Flat, primitive attributes for Sentry Logs and Metrics. */
export type TelemetryAttributes = Record<string, string | number | boolean>;
export type TelemetryLogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface TelemetrySink {
  capture(record: TelemetryRecord): void;
  breadcrumb(crumb: TelemetryBreadcrumb): void;
  /** Sentry Logs: a searchable timeline line. Never creates an issue. */
  log(level: TelemetryLogLevel, message: string, attributes: TelemetryAttributes): void;
  /** Sentry Metrics. */
  count(name: string, attributes: TelemetryAttributes): void;
  distribution(name: string, value: number, unit: 'millisecond', attributes: TelemetryAttributes): void;
  setUser(user: TelemetryUser | null): void;
  /** Label everything recorded from now on with this activity; null clears it. */
  setActivity(activity: TelemetryActivity | null): void;
  /** Null where replay is not available (background, popup). */
  replay: { start(): void; stop(): Promise<void> } | null;
}

const FLOW_LABELS: Record<string, string> = {
  start_service: 'Start Service',
  episode_creation: 'Episode Creation',
  send_link: 'Send Link',
  push_to_emr: 'Push to EMR'
};

function flowLabel(name: string): string {
  return FLOW_LABELS[name] ?? name.replace(/_/g, ' ');
}

let sink: TelemetrySink | null = null;
let user: TelemetryUser | null = null;
let patient: PatientContext | null = null;
let episode: EpisodeContext | null = null;
let facility: FacilityContext | null = null;

export function installTelemetrySink(next: TelemetrySink): void {
  sink = next;
  sink.setUser(user);
}

const PREFIX = '[Synexa]';

function consoleFor(level: LogLevel | 'debug'): (...args: unknown[]) => void {
  switch (level) {
    case 'error':
      return console.error;
    case 'warning':
      return console.warn;
    case 'info':
      return console.info;
    default:
      return console.debug;
  }
}

function hasKeys(ctx: LogContext | undefined): ctx is LogContext {
  return !!ctx && Object.keys(ctx).length > 0;
}

function writeConsole(level: LogLevel | 'debug', message: string, err?: unknown, ctx?: LogContext): void {
  const args: unknown[] = [`${PREFIX} ${message}`];
  if (err !== undefined) {
    args.push(err);
  }
  if (hasKeys(ctx)) {
    args.push(ctx);
  }
  consoleFor(level)(...args);
}

function record(
  level: LogLevel,
  message: string,
  err: unknown,
  ctx: LogContext | undefined,
  flow: ActiveFlow | null,
  fingerprint?: string[]
): void {
  if (!sink) {
    return;
  }
  try {
    sink.capture({
      level,
      message,
      error: err,
      context: ctx ?? {},
      patient: flow?.patient ?? patient,
      episode: flow?.episode ?? episode,
      facility,
      flow: flow ? { name: flow.name, flowId: flow.flowId, startedAt: flow.startedAt } : null,
      fingerprint
    });
  } catch {
    // Telemetry must never break the ribbon.
  }
}

function errorText(err: unknown): string | null {
  if (err === undefined || err === null) {
    return null;
  }
  if (err instanceof Error) {
    return `${err.name}: ${err.message}`;
  }
  return typeof err === 'string' ? err : null;
}

/** The readable context (who, which patient/episode, which flow) for a log line. */
function contextAttributes(flow: ActiveFlow | null, extra?: LogContext, err?: unknown): TelemetryAttributes {
  const out: TelemetryAttributes = {};
  const put = (key: string, value: LogPrimitive): void => {
    if (value !== null && value !== undefined && value !== '') {
      out[key] = value;
    }
  };
  const p = flow?.patient ?? patient;
  const e = flow?.episode ?? episode;
  put('flow', flow?.name);
  put('flowId', flow?.flowId);
  put('activity', flow ? flowLabel(flow.name) : null);
  put('patient.name', p?.name);
  put('patient.mrn', p?.mrn);
  put('patient.id', p?.patientId);
  put('episode.id', e?.episodeId);
  put('service', e?.serviceName);
  put('episode.status', e?.status);
  put('facility', facility?.facilityName);
  put('facility.id', facility?.facilityId);
  put('error', errorText(err));
  for (const [key, value] of Object.entries(extra ?? {})) {
    put(key, value);
  }
  return out;
}

function sendLog(level: TelemetryLogLevel, message: string, flow: ActiveFlow | null, extra?: LogContext, err?: unknown): void {
  try {
    sink?.log(level, message, contextAttributes(flow, extra, err));
  } catch {
    /* ignore */
  }
}

/** Metric dimensions stay low-cardinality: no patient or episode ids. */
function metricAttributes(extra: LogContext): TelemetryAttributes {
  const out: TelemetryAttributes = {};
  if (facility?.facilityName) {
    out['facility'] = facility.facilityName;
  }
  for (const [key, value] of Object.entries(extra)) {
    if (value !== null && value !== undefined && value !== '') {
      out[key] = value;
    }
  }
  return out;
}

function sendCount(name: string, extra: LogContext): void {
  try {
    sink?.count(name, metricAttributes(extra));
  } catch {
    /* ignore */
  }
}

function sendDuration(name: string, ms: number, extra: LogContext): void {
  try {
    sink?.distribution(name, ms, 'millisecond', metricAttributes(extra));
  } catch {
    /* ignore */
  }
}

function crumb(category: string, message: string, level: LogLevel, data?: LogContext): void {
  try {
    sink?.breadcrumb({ category, message, level, data });
  } catch {
    /* ignore */
  }
}

// ---- flows ----------------------------------------------------------------

/** Keeps the final toast / error state in the replay before recording stops. */
const REPLAY_TAIL_MS = 1500;
/** A flow the user walks away from is closed as abandoned after this long. */
const FLOW_TIMEOUT_MS = 5 * 60_000;

type FlowOutcome = 'ok' | 'fail' | 'cancelled' | 'abandoned';

export interface FlowStartContext {
  patient?: PatientContext | null;
  episode?: EpisodeContext | null;
  [key: string]: LogPrimitive | PatientContext | EpisodeContext | null | undefined;
}

export interface Flow {
  readonly name: string;
  readonly flowId: string;
  step(name: string, ctx?: LogContext): void;
  /** Attach or update context learned mid-flow (e.g. the episode just created). */
  setEpisode(next: EpisodeContext | null): void;
  succeed(ctx?: LogContext): void;
  /** The user backed out (closed the modal). Recorded, but not as a problem. */
  cancel(ctx?: LogContext): void;
  fail(err: unknown, ctx?: LogContext): void;
}

interface ActiveFlow {
  name: string;
  flowId: string;
  startedAt: number;
  patient: PatientContext | null;
  episode: EpisodeContext | null;
  context: LogContext;
  done: boolean;
  timeout: ReturnType<typeof setTimeout> | null;
}

let activeFlow: ActiveFlow | null = null;
/** Serialises replay start/stop so a new flow never races the previous tail. */
let replayQueue: Promise<void> = Promise.resolve();
let replayTail: ReturnType<typeof setTimeout> | null = null;

function newFlowId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function applyActivity(flow: ActiveFlow | null): void {
  try {
    sink?.setActivity(
      flow
        ? {
            flow: { name: flow.name, flowId: flow.flowId, startedAt: flow.startedAt },
            label: flowLabel(flow.name),
            patient: flow.patient,
            episode: flow.episode,
            facility
          }
        : null
    );
  } catch {
    /* ignore */
  }
}

/**
 * Stops the previous flow's replay, then labels and starts this one. The label
 * is applied only after the old replay's final upload, so that upload keeps the
 * previous flow's label.
 */
function startReplay(flow: ActiveFlow): void {
  const replay = sink?.replay;
  if (replayTail) {
    clearTimeout(replayTail);
    replayTail = null;
  }
  if (!replay) {
    applyActivity(flow);
    return;
  }
  replayQueue = replayQueue
    .then(() => replay.stop())
    .then(() => {
      if (activeFlow === flow) {
        // Re-assert the signed-in user too, so a replay never starts anonymous.
        sink?.setUser(user);
        applyActivity(flow);
        replay.start();
      }
    })
    .catch(() => undefined);
}

/** Keeps recording briefly (final toast), uploads, then clears the activity label. */
function stopReplayAfterTail(): void {
  const replay = sink?.replay;
  if (!replay) {
    applyActivity(null);
    return;
  }
  if (replayTail) {
    clearTimeout(replayTail);
  }
  replayTail = setTimeout(() => {
    replayTail = null;
    replayQueue = replayQueue
      .then(() => replay.stop())
      .then(() => {
        if (!activeFlow) {
          applyActivity(null);
        }
      })
      .catch(() => undefined);
  }, REPLAY_TAIL_MS);
}

function splitFlowContext(ctx: FlowStartContext | undefined): {
  patient: PatientContext | null;
  episode: EpisodeContext | null;
  context: LogContext;
} {
  const context: LogContext = {};
  let p: PatientContext | null = null;
  let e: EpisodeContext | null = null;
  for (const [key, value] of Object.entries(ctx ?? {})) {
    if (key === 'patient') {
      p = (value as PatientContext | null) ?? null;
    } else if (key === 'episode') {
      e = (value as EpisodeContext | null) ?? null;
    } else if (value === null || typeof value !== 'object') {
      context[key] = value as LogPrimitive;
    }
  }
  return { patient: p, episode: e, context };
}

function finishFlow(flow: ActiveFlow, outcome: FlowOutcome, err: unknown, ctx: LogContext | undefined): void {
  if (flow.done) {
    return;
  }
  flow.done = true;
  if (flow.timeout) {
    clearTimeout(flow.timeout);
  }
  const durationMs = Date.now() - flow.startedAt;
  const merged: LogContext = { ...flow.context, ...ctx, outcome, durationMs };

  if (outcome === 'fail') {
    writeConsole('error', `Flow ${flow.name} failed`, err, merged);
    sendLog('error', `${flowLabel(flow.name)} failed`, flow, merged, err);
    record('error', `Flow ${flow.name} failed`, err ?? new Error(`Flow ${flow.name} failed`), merged, flow);
  } else {
    const level: LogLevel = outcome === 'abandoned' ? 'warning' : 'info';
    writeConsole(level === 'info' ? 'debug' : 'warning', `Flow ${flow.name} ${outcome}`, undefined, merged);
    sendLog(level === 'info' ? 'info' : 'warn', `${flowLabel(flow.name)} ${outcome}`, flow, merged);
    // One issue per flow outcome per signed-in user, titled with their name, e.g.
    // 'flow.send_link.ok · Sara Khan'. Failures (above) keep grouping by error, so
    // one bug hitting many users stays one issue.
    const who = user?.username || user?.email || 'Unknown user';
    record(
      level,
      `flow.${flow.name}.${outcome} · ${who}`,
      undefined,
      merged,
      flow,
      ['flow', flow.name, outcome, user?.id ?? 'anonymous']
    );
  }
  sendCount('flow.completed', { flow: flow.name, outcome });
  sendDuration('flow.duration', durationMs, { flow: flow.name, outcome });

  if (activeFlow === flow) {
    activeFlow = null;
    stopReplayAfterTail();
  }
}

function startFlow(name: string, ctx?: FlowStartContext): Flow {
  if (activeFlow && !activeFlow.done) {
    finishFlow(activeFlow, 'abandoned', undefined, { supersededBy: name });
  }

  const split = splitFlowContext(ctx);
  const flow: ActiveFlow = {
    name,
    flowId: newFlowId(),
    startedAt: Date.now(),
    patient: split.patient ?? patient,
    episode: split.episode ?? episode,
    context: split.context,
    done: false,
    timeout: null
  };
  flow.timeout = setTimeout(() => finishFlow(flow, 'abandoned', undefined, { reason: 'timeout' }), FLOW_TIMEOUT_MS);
  activeFlow = flow;

  startReplay(flow);
  crumb('flow', `${name}.start`, 'info', { flowId: flow.flowId, ...split.context });
  sendLog('info', `${flowLabel(name)} started`, flow, split.context);
  writeConsole('debug', `Flow ${name} started`, undefined, split.context);

  return {
    name,
    flowId: flow.flowId,
    step(stepName, stepCtx) {
      if (flow.done) {
        return;
      }
      const stepData: LogContext = { elapsedMs: Date.now() - flow.startedAt, ...stepCtx };
      crumb('flow', `${name}.${stepName}`, 'info', { flowId: flow.flowId, ...stepData });
      sendLog('info', `${flowLabel(name)}: ${stepName}`, flow, stepData);
    },
    setEpisode(next) {
      flow.episode = next;
      if (activeFlow === flow) {
        applyActivity(flow);
      }
    },
    succeed(okCtx) {
      finishFlow(flow, 'ok', undefined, okCtx);
    },
    cancel(cancelCtx) {
      finishFlow(flow, 'cancelled', undefined, cancelCtx);
    },
    fail(err, failCtx) {
      finishFlow(flow, 'fail', err, failCtx);
    }
  };
}

// ---- public API -------------------------------------------------------------

function currentFlow(): ActiveFlow | null {
  return activeFlow && !activeFlow.done ? activeFlow : null;
}

export const logger = {
  debug(message: string, ctx?: LogContext): void {
    writeConsole('debug', message, undefined, ctx);
  },

  /** Console + a Sentry log line. */
  info(message: string, ctx?: LogContext): void {
    writeConsole('info', message, undefined, ctx);
    crumb('log', message, 'info', ctx);
    sendLog('info', message, currentFlow(), ctx);
  },

  /** Console + a Sentry log line + a Sentry message (issue). */
  warn(message: string, err?: unknown, ctx?: LogContext): void {
    writeConsole('warning', message, err, ctx);
    sendLog('warn', message, currentFlow(), ctx, err);
    record('warning', message, err, ctx, currentFlow());
  },

  /** Console + a Sentry log line + a Sentry exception (issue). */
  error(message: string, err?: unknown, ctx?: LogContext): void {
    writeConsole('error', message, err, ctx);
    sendLog('error', message, currentFlow(), ctx, err);
    record('error', message, err ?? new Error(message), ctx, currentFlow());
  },

  /** User activity ('ribbon.opened', 'episode.detail.opened'): breadcrumb + log line, no issue. */
  event(name: string, ctx?: LogContext): void {
    crumb('ui', name, 'info', ctx);
    sendLog('info', `ui.${name}`, currentFlow(), ctx);
  },

  /** An API call (from the fetch wrapper / interceptor): breadcrumb, log line and metrics. */
  http(data: { method: string; path: string; status: number; durationMs: number }): void {
    const failed = data.status >= 400 || data.status === 0;
    crumb('http', `${data.method} ${data.path}`, failed ? 'warning' : 'info', data);
    sendLog(failed ? 'warn' : 'info', `HTTP ${data.method} ${data.path} ${data.status}`, currentFlow(), data);
    const statusClass = data.status === 0 ? 'network_error' : `${Math.floor(data.status / 100)}xx`;
    const dims: LogContext = { method: data.method, endpoint: data.path, status_class: statusClass };
    sendCount('api.request', dims);
    sendDuration('api.duration', data.durationMs, dims);
  },

  setUser(next: TelemetryUser | null): void {
    user = next;
    sink?.setUser(next);
  },

  setPatient(next: PatientContext | null): void {
    patient = next;
  },

  setEpisode(next: EpisodeContext | null): void {
    episode = next;
  },

  setFacility(next: FacilityContext | null): void {
    facility = next;
  },

  startFlow
};
